import {FC, useEffect, useMemo} from "react";
import {Box, BoxProps, ThemeProvider} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {DataGridPro, GridColumns} from "@mui/x-data-grid-pro";
import {useTranslation} from "react-i18next";
import type {CourseDetailsDto, GetCourseCurriculumResponse, CurriculumModuleDto} from "@/app2/shared/data";
import {AdminHeader} from "@/pages/ManagerView/AdminHeader";
import {palette} from "@/constants";
import {adminTheme as createAdminTheme} from "@/templates/adminTheme";
import {convertSecondsToHHMMSS} from "@/utils";

const useStyles = makeStyles()((theme) => ({
	contentBox: {
		marginTop: theme.spacing(3),
		padding: theme.spacing(2.5),
		backgroundColor: palette.greyDark,
		borderWidth: 0,
		borderRadius: theme.borderRadius.small,
		"& .MuiButton-text": {
			color: theme.palette.primary.main,
			border: "0px solid",
			borderRadius: "0",
			fontWeight: "600",
			fontSize: "0.8125rem",
			textTransform: "uppercase",
			padding: "12px 5px 12px",
			letterSpacing: "0.025rem",
			"&:hover": {
				background: "transparent",
				opacity: "0.8"
			},
			"& span": {
				marginRight: 0,
				background: "transparent"
			},
			"& svg": {
				marginRight: "8px",
				"& path": {
					fill: `${theme.palette.primary.main} !important`
				}
			},
			"&:disabled": {
				color: theme.palette.primary.main,
				opacity: 0.5,
				"& svg": {
					"& path": {
						fill: theme.palette.primary.main
					}
				}
			}
		},
		"& [class*=\"customToolbar\"]": {
			display: "none !important"
		}
	},
	lessonsAdminTable: {
		padding: 0
	}
}));

interface CourseLessonsTabProps {
	course: CourseDetailsDto;
	curriculum?: GetCourseCurriculumResponse;
	isLoading: boolean;
}

export type IProps = Omit<BoxProps, "children"> & CourseLessonsTabProps;

/**
 * Course Lessons Tab
 * 
 * Displays curriculum modules in a DataGrid table.
 * Matches the design of the original LessonsTab.
 */
export const CourseLessonsTab: FC<IProps> = ({course, curriculum, isLoading, ...baseProps}) => {
	const {classes} = useStyles();
	const {t} = useTranslation();

	const headCells: GridColumns<CurriculumModuleDto> = useMemo(
		() => [
			{
				field: "title",
				type: "string",
				flex: 1,
				headerName: t("course_lessons_table_title"),
				minWidth: 240
			},
			{
				field: "type",
				type: "string",
				flex: 1,
				headerName: t("course_lessons_table_type"),
				valueFormatter: (params) => {
					if (!params.value) return "";
					return String(params.value).charAt(0).toUpperCase() + String(params.value).slice(1);
				}
			},
			{
				field: "duration",
				type: "string",
				flex: 1,
				headerName: t("course_lessons_table_duration"),
				valueGetter: (params) => {
					const duration = params.row.duration;
					return duration ? convertSecondsToHHMMSS(duration) : "";
				}
			}
		],
		[t]
	);

	const adminTheme = useMemo(() => createAdminTheme(), []);

	return (
		<Box {...baseProps}>
			<Box className={classes.contentBox}>
				<AdminHeader title={`${course?.title} ${t("course_lessons_title")}`} />
				<ThemeProvider theme={adminTheme}>
					<DataGridPro<CurriculumModuleDto>
						className={classes.lessonsAdminTable}
						initialState={{
							sorting: {
								sortModel: [{field: "order", sort: "asc"}]
							}
						}}
						localeText={{
							footerTotalRows: t("course_lessons_table_total_rows")
						}}
						aria-label="admin table"
						loading={isLoading}
						keepNonExistentRowsSelected
						rowCount={curriculum?.items?.length ?? 0}
						rows={curriculum?.items ?? []}
						columns={headCells}
						checkboxSelection={false}
						autoHeight
					/>
				</ThemeProvider>
			</Box>
		</Box>
	);
};
